import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import { saveAs } from 'file-saver';
import { 
    Person as PersonIcon,
    Send as SendIcon,
    Update as UpdateIcon,
    AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import Menu from '@mui/material/Menu/Menu';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import {
    Button,
    SearchBar,
    MemberList,
    Theme,
    EmployeeModalHandler,
    MessageCard,
    CardBlock,
    TerminateEmployeeModal,
    Services,
    Text,
    MassUpdate,
    i18n,
    tokens,
    MassSalaryUpdateModal
} from '@simplybenefits/sb-ui-library';
import FilterForm from './FilterForm.js';
import NotifyDialog from './NotifyDialog.js';
import permissionLevel from '../../../services/permissions.js';
import {
    getUsers,
    addUser,
    updateUser,
    removeUser,
    uploadMassUpdateForm,
    uploadMassSalaryForm,
    downloadSalaryUpdateData
} from '../../../actions/accounts.js';

/**
 * Search through an array of members
 * @param {*} employees
 * @param {*} query
 */
const searchMembers = (employees, query) => {
    const employeeList = [];
    const searchText = query?.toLowerCase().trim();

    if (searchText !== '') {
        employees.map((member) => {
            if (`${ member.firstname } ${ member.lastname }`.toLowerCase().trim()
                .includes(searchText) ||
                        member.email.toLowerCase().trim()
                            .includes(searchText) ||
                        member.cert === searchText
            ) {
                return employeeList.push(member);
            }

            return null;
        });
    } else {
        employees.map((member) => employeeList.push(member));
    }

    return employeeList;
};

class Members extends PureComponent {
    state = {
        showMassUpdateMenu: false,
        showMassSalaryUpdateModal: false,
        openMassUpdateModal: false,
        massUpdateType: null,
        anchorEl: null,
        showNewEmployee: false,
        showClasses: false,
        showDivisions: false,
        currentEmployee: null,
        employeeList: this.props.employees,
        notificationModalOpen: false,
        notificationUser: null,
        query: ''
    };

    static getDerivedStateFromProps(props, prevState) {
        if (!permissionLevel('Employees', props.accessType).includes('read')) {
            window.location.href = '/403';
        }

        if (props.employees !== prevState.employeeList) {
            const employeeList = searchMembers(props.employees, prevState.query);

            return {
                employeeList: [ ...employeeList ]
            };
        }

        return null;
    }

    _editEmployee = (employee) => {
        this.setState({
            showNewEmployee: true,
            currentEmployee: { ...employee }
        });
    };

    _terminateEmployee = (employee) => {
        this.setState({
            showTerminateMemberModal: true,
            currentEmployee: { ...employee }
        });
    };

    _manageEmployee = (employee) => {
        const win = window.open(`/members/${ employee.userID }`, '_blank');
        win.focus();
    };

    _toggleShowEmployee = () => {
        this.setState({
            showNewEmployee: !this.state.showNewEmployee,
            currentEmployee: null
        });
    };

    _showNewEmployee = () => {
        this.setState({
            showMassUpdateMenu: false,
            showMassSalaryUpdateModal: false,
            showNewEmployee: true,
            currentEmployee: null,
            showDivisions: false,
            showClasses: false
        });
    };

    _downloadTemplate = (selectedType) => {
        saveAs(selectedType.templateDownloadLink, `${ selectedType.label }.csv`);
    };

    _toggleMassUpdateMenu = (event) => {
        this.setState({
            anchorEl: !this.state.showMassUpdateMenu ? event.currentTarget : null,
            showMassUpdateMenu: !this.state.showMassUpdateMenu
        });
    };

    _toggleMassSalaryUpdateModal = (event) => {
        this.setState({
            anchorEl: !this.state.showMassSalaryUpdateModal ? event.currentTarget : null,
            showMassSalaryUpdateModal: !this.state.showMassSalaryUpdateModal
        });
    };

    _toggleMassUpdateModal = (type = null) => {
        this.setState({
            anchorEl: null,
            showMassUpdateMenu: false,
            showMassSalaryUpdateModal: false,
            massUpdateType: type,
            openMassUpdateModal: !this.state.openMassUpdateModal
        });
    };

    _search = async (query, members = null) => {
        const membersList = members ? members : this.props.employees;
        const employeeList = searchMembers(membersList, query);

        await this.setState({
            employeeList: [ ...employeeList ]
        });
    };

    _searchInput = (event) => {
        this.setState({
            query: event.target.value
        });
    };

    /**
     * Compare function for sorting class list
     * @param a
     * @param b
     * @returns {number}
     * @private
     */
    _compareclassData = (a, b) => {
        const classA = this.props.classData[a];
        const classB = this.props.classData[b];

        // Use toUpperCase() to ignore character casing
        const valA = classA.title.toUpperCase();
        const valB = classB.title.toUpperCase();

        let comparison = 0;

        if (valA > valB) {
            comparison = 1;
        } else if (valA < valB) {
            comparison = -1;
        }

        return comparison;
    };

    _toggleNotificationModal = (userID) => {
        this.setState({
            notificationModalOpen: !this.state.notificationModalOpen,
            notificationUser: userID
        });
    };

    _applyFilters = (filters) => {
        this.props.dispatch(getUsers(this.props.currentAccountMeta.accountID, filters));
    };

    render() {
        const { classData } = this.props;
        const isStrictMarketplaceOnly = this.props.currentAccountMeta.isMarketplaceOnly && !this.props.currentAccountMeta.isPrivateLabel;

        const styles = {
            memberCard: {
                cursor: 'pointer',
                marginBottom: '0',
                minHeight: '50px',
                padding: '10px 10px 10px 10px'
            },
            memberListHeader: {
                backgroundColor: Theme.colors.surface,
                borderLeft: `${ Theme.colors.surface } solid 5px`,
                boxShadow: '0 1px 2px rgba(0, 0, 0, .15)',
                cursor: 'pointer',
                height: 'auto',
                marginBottom: '0',
                minHeight: '70px',
                padding: '10px',
                position: 'relative',
                zIndex: 4,
                borderBottom: '2px solid rgba(0, 0, 0, 0.09)'
            }
        };

        return (
            <React.Fragment>
                <Grid
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-start"
                    style={{ marginTop: 20, marginBottom: 10 }}
                >
                    <div>
                        <Button transparent onClick={() => this._toggleNotificationModal(null)} >
                            <SendIcon style={{ fontSize: 17, lineHeight: '14px', color: 'inherit' }} />&nbsp;
                            Notify Members
                        </Button>

                        <Button
                            transparent
                            onClick={this._showNewEmployee}
                        >
                            <PersonIcon />&nbsp;New Member
                        </Button>

                        {
                            this.props.currentAccountMeta.isStudentGroup ?
                                <Button
                                    transparent
                                    onClick={this._toggleMassUpdateMenu}
                                >
                                    <UpdateIcon />&nbsp;Mass Update
                                </Button>
                                : null
                        }

                        {
                            (!this.props.currentAccountMeta.isStudentGroup && !isStrictMarketplaceOnly) ?
                                <Button
                                    transparent
                                    onClick={this._toggleMassSalaryUpdateModal}
                                >
                                    <AttachMoneyIcon />&nbsp;Salary Update
                                </Button>
                                : null
                        }

                    </div>

                    <div style={{ marginLeft: 'auto' }}>
                        <SearchBar
                            onChange={(e) => this._searchInput(e)}
                            search={(e) => this._search(e)}
                            customStyle={{ backgroundColor: 'white' }}
                        />
                    </div>
                </Grid>

                {
                    (this.props.currentAccountMeta.blockEnrollments === 1 && !this.props.currentAccountMeta.isStudentGroup) &&
                        <MessageCard
                            title="Account Pending"
                            subtitle="This account is still pending review. During this time, sending out enrollments is disabled. However, you are free to manage the member list."
                        />
                }

                {
                    this.state.showMassUpdateMenu ?
                        <Menu
                            anchorEl={this.state.anchorEl}
                            open={this.state.showMassUpdateMenu}
                            onClose={this._toggleMassUpdateMenu}
                            disableAutoFocusItem={true}
                            anchorOrigin={ { vertical: "top", horizontal: "left" } }
                        >

                            {
                                Object.entries(Services.massUpdateTypes).map(([ type, { label }]) => (
                                    <MenuItem
                                        key={ type }
                                        style={ styles.navDropMenuItem }
                                        onClick={() => this._toggleMassUpdateModal(type)}
                                    >
                                        <Text
                                            align="center"
                                            medium
                                            display="block"
                                            customLinkStyle={{ textDecoration: 'none' }}
                                        >
                                            { label }
                                        </Text>
                                    </MenuItem>
                                ))
                            }
                        </Menu>
                        : null
                }

                {
                    this.state.openMassUpdateModal ?
                        <MassUpdate
                            type={ this.state.massUpdateType }
                            close={ this._toggleMassUpdateModal }
                            upload={ (file) => this.props.dispatch(uploadMassUpdateForm(this.props.accountID, file)) }
                            download={ this._downloadTemplate }
                        />
                        : null
                }

                {
                    this.state.showMassSalaryUpdateModal ?
                        <MassSalaryUpdateModal
                            maxBackdateMonths={1}
                            errorMessage={i18n.t(tokens.labels.employees.backdatedChangesTwoMonths)}
                            close={ this._toggleMassSalaryUpdateModal }
                            downloadSalaryUpdateData={(...params) => this.props.dispatch(downloadSalaryUpdateData(this.props.currentAccountMeta.accountID, ...params)) }
                            uploadMassSalaryForm={(...params) => this.props.dispatch(uploadMassSalaryForm(this.props.currentAccountMeta.accountID, ...params)) }
                        />
                        : null
                }

                {
                    this.state.showNewEmployee &&
                        <EmployeeModalHandler
                            open={this.state.showNewEmployee}
                            isEdit={Boolean(this.state.currentEmployee)}
                            close={this._toggleShowEmployee}
                            currentEmployee={this.state.currentEmployee}
                            classData={this.props.classData}
                            divisions={this.props.divisions}
                            accountID={this.props.accountID}
                            currentAccountMeta={ this.props.currentAccountMeta }
                            isMasterSite={ true }
                            addUser={ (...params) => this.props.dispatch(addUser(...params)) }
                            updateUser={ (...params) => this.props.dispatch(updateUser(...params)) }
                            removeUser={ (...params) => this.props.dispatch(removeUser(...params)) }
                        />
                }

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                        <FilterForm
                            classData={ classData }
                            divisions={ this.props.divisions }
                            applyFilters= { this._applyFilters }
                            currentAccountMeta={ this.props.currentAccountMeta }
                        />
                    </Grid>

                    <Grid item xs={12} sm={9}>
                        {
                            this.state.employeeList.length === 0 ?
                                <CardBlock
                                    customStyle={ styles.memberCard }
                                    id="account-employee-card"
                                    style={{
                                        fontWeight: 600,
                                        textAlign: 'center',
                                        padding: 30,
                                        minWidth: '100%'
                                    }}
                                >
                                    No Members
                                </CardBlock>
                                :
                                <MemberList
                                    employees={ this.state.employeeList }
                                    divisions={ this.props.divisions }
                                    classesData={ this.props.classData }
                                    toggleNotificationModal={ this._toggleNotificationModal }
                                    editEmployee={ this._editEmployee }
                                    terminateEmployee={ this._terminateEmployee }
                                    manageEmployee={ this._manageEmployee }
                                    isMasterSite
                                />
                        }
                    </Grid>
                </Grid>

                <NotifyDialog
                    notificationModalOpen={this.state.notificationModalOpen}
                    toggleNotificationModal={() => this._toggleNotificationModal(null)}
                    notificationUser={this.state.notificationUser}
                    accountID={this.props.accountID}
                    blockEnrollments={this.props.currentAccountMeta.blockEnrollments}
                    currentAccountMeta={ this.props.currentAccountMeta }
                />

                <TerminateEmployeeModal
                    open={this.state.showTerminateMemberModal}
                    currentEmployee={this.state.currentEmployee}
                    terminate={(...params) => this.props.dispatch(removeUser(...params))}
                    close={ () => this.setState({ showTerminateMemberModal: false })}
                    loader={this.props.loader}
                />
            </React.Fragment>
        );
    }
}

Members.propTypes = {
    employees: PropTypes.array,
    classData: PropTypes.object,
    divisions: PropTypes.object,
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    employees: state.accounts.currentAccount.employees,
    classData: state.accounts.currentAccount.classes,
    divisions: state.accounts.currentAccount.divisions,
    currentAccountMeta: state.accounts.currentAccount.meta,
    accessType: state.master.access
});

export default connect(mapStateToProps)(Members);